import React, { useState, useMemo } from "react";
import { Search, BookOpen, ChevronRight, ExternalLink, Image as ImageIcon, Lightbulb, X } from "lucide-react";
import { PageHeader, Card } from "../components/UI";
import { TRAINING_GROUPS, TRAINING_MODULES } from "./training/content";

export default function Training() {
  const firstSlug = TRAINING_GROUPS[0].modules[0];
  const [selected, setSelected] = useState(firstSlug);
  const [search, setSearch] = useState("");
  const [zoomImg, setZoomImg] = useState(null);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return TRAINING_GROUPS;
    const q = search.trim().toLowerCase();
    return TRAINING_GROUPS.map((g) => ({
      ...g,
      modules: g.modules.filter((m) => {
        const mod = TRAINING_MODULES[m];
        if (!mod) return false;
        return (
          mod.title.toLowerCase().includes(q) ||
          (mod.purpose || "").toLowerCase().includes(q) ||
          m.includes(q)
        );
      }),
    })).filter((g) => g.modules.length > 0);
  }, [search]);

  const mod = TRAINING_MODULES[selected];

  return (
    <div data-testid="training-page">
      <PageHeader
        kicker="Help Center"
        title="Training & Product Guide"
        subtitle="A screen-by-screen walkthrough of every module on the platform. Search a module on the left, then explore each section, drop-down and field on the right."
        testId="training-header"
      />

      <div className="grid grid-cols-12 gap-6">
        {/* Left — module nav */}
        <aside className="col-span-12 md:col-span-4 lg:col-span-3" data-testid="training-nav">
          <div className="surface p-3 sticky top-4">
            <div className="relative mb-3">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search modules…"
                className="w-full pl-8 pr-3 py-1.5 text-[12px] bg-slate-50 border-slate-200 rounded"
                data-testid="training-search"
              />
            </div>
            <div className="max-h-[70vh] overflow-y-auto pr-1">
              {filteredGroups.length === 0 && (
                <div className="text-center text-slate-400 text-[12px] py-6">No modules matched.</div>
              )}
              {filteredGroups.map((g) => (
                <div key={g.group} className="mb-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 px-2 mb-1">{g.group}</div>
                  {g.modules.map((slug) => {
                    const m = TRAINING_MODULES[slug];
                    if (!m) return null;
                    const active = selected === slug;
                    return (
                      <button
                        key={slug}
                        onClick={() => setSelected(slug)}
                        className={`w-full text-left flex items-center justify-between gap-2 px-2 py-1.5 rounded text-[12.5px] transition-colors ${
                          active ? "bg-brand-navy text-white" : "text-slate-700 hover:bg-slate-100"
                        }`}
                        data-testid={`training-link-${slug}`}
                      >
                        <span className="truncate">{m.title}</span>
                        <ChevronRight size={12} className={active ? "opacity-80" : "opacity-40"} />
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Right — module detail */}
        <main className="col-span-12 md:col-span-8 lg:col-span-9" data-testid="training-detail">
          {!mod ? (
            <Card><div className="text-slate-500 text-[13px]">Select a module from the left.</div></Card>
          ) : (
            <div className="space-y-5">
              {/* Header */}
              <div className="surface p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="small-caps text-teal-700 flex items-center gap-1"><BookOpen size={12} /> Module guide</div>
                    <h2 className="serif-display text-3xl text-slate-900 mt-1" data-testid="training-title">{mod.title}</h2>
                    <p className="text-slate-600 text-[14px] mt-2 max-w-3xl leading-relaxed">{mod.purpose}</p>
                    {mod.audience && (
                      <div className="mt-3 text-[12px] text-slate-500">
                        <span className="small-caps text-slate-400 mr-1">Who can access</span>
                        <span className="text-slate-700">{mod.audience}</span>
                      </div>
                    )}
                  </div>
                  <a
                    href={mod.route}
                    className="btn-teal flex items-center gap-2 !py-1.5"
                    data-testid="training-open-module"
                  >
                    Open module <ExternalLink size={12} />
                  </a>
                </div>
              </div>

              {/* Screenshot */}
              <div className="surface p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-widest text-slate-400 flex items-center gap-1">
                    <ImageIcon size={12} /> Screen reference
                  </div>
                  <div className="text-[11px] text-slate-400">Captured from this preview build</div>
                </div>
                <button
                  type="button"
                  onClick={() => mod.screenshot && setZoomImg(`/training/screenshots/${mod.screenshot}`)}
                  className="block w-full border border-slate-200 rounded overflow-hidden bg-slate-50 hover:opacity-90 transition"
                  data-testid="training-screenshot-btn"
                >
                  <img
                    src={`/training/screenshots/${mod.screenshot}`}
                    alt={`${mod.title} screenshot`}
                    className="w-full h-auto block"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.parentElement.insertAdjacentHTML(
                        "beforeend",
                        '<div class="text-center text-slate-400 text-[12px] py-16">Screenshot not generated yet. Run the capture script (see Training README) to populate this image.</div>'
                      );
                    }}
                  />
                </button>
              </div>

              {/* Sections */}
              <div className="grid grid-cols-1 gap-4">
                {mod.sections.map((s, i) => (
                  <Card key={i} title={s.title} testId={`training-section-${i}`}>
                    {s.body && <p className="text-slate-600 text-[13.5px] leading-relaxed">{s.body}</p>}
                    {s.items && s.items.length > 0 && (
                      <ul className="mt-3 space-y-2 text-[13px] text-slate-700">
                        {s.items.map((it, j) => (
                          <li key={j} className="flex gap-2">
                            <span className="text-teal-700 font-medium mt-0.5">›</span>
                            <span className="leading-relaxed">{it}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                ))}
              </div>

              {/* Tips */}
              {mod.tips && mod.tips.length > 0 && (
                <div className="rounded border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-center gap-2 text-amber-900 font-medium text-[13px] mb-2">
                    <Lightbulb size={14} /> Tips & Gotchas
                  </div>
                  <ul className="space-y-1.5 text-[13px] text-amber-900/90">
                    {mod.tips.map((t, i) => (
                      <li key={i} className="flex gap-2"><span className="opacity-60">•</span><span>{t}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Zoom modal */}
      {zoomImg && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6"
          onClick={() => setZoomImg(null)}
          data-testid="training-zoom-modal"
        >
          <button
            onClick={() => setZoomImg(null)}
            className="absolute top-4 right-4 text-white hover:text-amber-200"
            data-testid="training-zoom-close"
          >
            <X size={24} />
          </button>
          <img src={zoomImg} alt="Zoomed screenshot" className="max-h-[90vh] max-w-[95vw] object-contain rounded shadow-2xl" />
        </div>
      )}
    </div>
  );
}
